import React from "react";
import { EntityDeltaItem } from "../../../../../../Rest_api/RestApi";

import loader from "../../../../../../components/recharts/loader.gif";

// Header Cell component with orange color styling
// Header Cell component with orange color styling
interface HeaderCellProps {
    title: React.ReactNode;
    width?: string;
}

const HeaderCell: React.FC<HeaderCellProps> = ({ title, width }) => (
    <th
        className="py-2 text-xs px-2 text-center font-medium text-[#F1935C] whitespace-nowrap"
        style={{ width: width }}
    >
        {title}
    </th>
);

interface SummaryTableInjectionProps {
    data?: EntityDeltaItem[];
    hideOutage?: boolean;
    loading?: boolean;
}

const SummaryTableInjection: React.FC<SummaryTableInjectionProps> = ({
    data,
    hideOutage = false,
    loading = false,
}) => {
    // Helper to format values with sign for deltas
    const formatDeltaVal = (val?: number) => {
        if (val === undefined) return "-";
        const formatted = Math.abs(val).toFixed(2);
        return val >= 0 ? `+${formatted}` : `-${formatted}`;
    };

    // Helper to format absolute values (no sign enforcement)
    const formatAbsVal = (val?: number) => val !== undefined ? val.toFixed(2) : "-";

    // Helper for color
    const getColor = (val?: number) => {
        if (val === undefined) return "text-[#8C8A96]";
        return val >= 0 ? "text-[#22C55E]" : "text-[#F1935C]";
    };

    if (loading) {
        return (
            <div className="w-full bg-white flex items-center justify-center min-h-[250px]">
                <img src={loader} width={200} alt="loader" className="block" />
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="w-full bg-white flex items-center justify-center min-h-[250px]">
                <p className="text-gray-500">No data available</p>
            </div>
        );
    }

    return (
        <div className="w-full" style={{ maxHeight: "350px", overflowY: "auto" }}>
            <table id="summary-injection-table" className="w-full table-fixed">
                <thead className="sticky top-0 z-[1] border-b border-[#DFEAF2] bg-white">
                    <tr className="text-sm">
                        <HeaderCell title="Utility" />
                        <HeaderCell title="Injection Δ (MWh)" />
                        <HeaderCell title="Temp Δ (°C)" />
                        <HeaderCell title="Precipitation (mm)" />
                        <HeaderCell title="Solar Intensity (W/m²)" width="160px" />
                        {!hideOutage && <HeaderCell title="Outage Δ (MW)" />}
                        {!hideOutage && <HeaderCell title="Total Outage (MW)" />}
                        <HeaderCell title="Load Δ (MW)" />
                    </tr>
                </thead>
                <tbody className="text-[#8C8A96] font-normal text-xs">
                    {data.map((row, index) => (
                        <tr
                            key={index}
                            className="border-b border-gray-50 hover:bg-gray-50"
                        >
                            <td className="py-2 px-2 text-center whitespace-nowrap font-medium text-[#525062]">
                                {row.utility}
                            </td>
                            <td className={`py-2 px-2 text-center whitespace-nowrap ${getColor(row.injection_delta_mwh)}`}>
                                {formatDeltaVal(row.injection_delta_mwh)}
                            </td>
                            <td className={`py-3 px-2 text-center whitespace-nowrap ${getColor(row.temp_delta)}`}>
                                {formatDeltaVal(row.temp_delta)}
                            </td>
                            <td className={`py-3 px-2 text-center whitespace-nowrap ${getColor(row.precipitation_delta)}`}>
                                {formatDeltaVal(row.precipitation_delta)}
                            </td>
                            <td className={`py-3 px-2 text-center whitespace-nowrap ${getColor(row.solar_intensity_delta)}`}>
                                {formatDeltaVal(row.solar_intensity_delta)}
                            </td>
                            {!hideOutage && (
                                <td className={`py-3 px-2 text-center whitespace-nowrap ${getColor(row.outage_delta_mw)}`}>
                                    {formatDeltaVal(row.outage_delta_mw)}
                                </td>
                            )}
                            {!hideOutage && (
                                <td className="py-3 px-2 text-center whitespace-nowrap text-[#8C8A96]">
                                    {formatAbsVal(row.total_outage_mw)}
                                </td>
                            )}
                            <td className={`py-3 px-2 text-center whitespace-nowrap ${getColor(row.load_delta_mw)}`}>
                                {formatDeltaVal(row.load_delta_mw)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default SummaryTableInjection;
